import { useCallback, useEffect, useRef } from 'react';

import { WIDGET_ANIMATION_TYPE_HAS_DIRECTION } from '../../../../../constants/scenarioConstant';

import { WidgetProps } from './WidgetProps';

// Press ("active") variants: a click on a pill/square button plays this instead of
// the plain exit, then resumes the loop.
const ANIMATION_ACTIVE_SUFFIX: Partial<Record<string, string>> = {
	pillOut: 'pillBgActive',
	squareOut: 'squarePressActive',
};

// The press sequence runs longer than a plain out; scale its duration accordingly.
const ACTIVE_DURATION_RATIO: Partial<Record<string, number>> = {
	pillOut: 1.05 / 0.467,
	squareOut: 1.35 / 0.65,
};

// Exit animations that have a press ("active") state - the editor shows an
// active-colour picker for these.
export const EXIT_ANIMATION_HAS_ACTIVE_STATE = Object.keys(ANIMATION_ACTIVE_SUFFIX);

// Reference durations (ms) from the client's Lottie files, used as the defaults for
// pill/square so their timing matches the source.
export const WIDGET_ANIMATION_DEFAULT_DURATIONS: Partial<Record<string, number>> = {
	pillIn: 617,
	squareIn: 1017,
	pillOut: 467,
	squareOut: 650,
};

// On press the background flashes to the active colour over this long. It is a
// PAINT change (background-color) so it is Safari-safe inside the foreignObject.
const ACTIVE_COLOR_FLASH_MS = 80;

// IMPORTANT (Safari / WebKit foreignObject) - two separate rules:
// 1. WHOLE-WIDGET animation (pill/square scale, loop, slide, zoom) runs on the SVG
//    <g> (gElementRef), NEVER on the .button-widget div. A `transform` or `opacity`
//    animation - or even `position: relative/absolute` - on HTML INSIDE the
//    foreignObject promotes it to a compositing layer that WebKit positions in the
//    wrong coordinate space, so the widget/content flies off. (getBoundingClientRect
//    still reports the correct box, so this is ONLY visible on real Safari.)
// 2. CONTENT sub-motion (text rises, icon slides in) runs on the text/icon HTML but
//    is animated with `margin` ONLY - a layout property that does NOT create a
//    compositing layer, so WebKit paints it in place. No transform / opacity /
//    position on content, or it flies off (verified on real Safari).

type AnimEl = HTMLElement | SVGElement;

const clearAnimation = (el: AnimEl | null | undefined) => {
	if (!el) { return; }
	Array.from(el.classList)
		.filter((c) => c.startsWith('animate__'))
		.forEach((c) => el.classList.remove(c));
	el.style.removeProperty('animation-delay');
	el.style.removeProperty('animation-duration');
	el.style.removeProperty('animation-fill-mode');
};

// Build the animate.css class for an entry/exit animation.
// - slideAndFade has NO animate.css keyframes (animate__slideAndFadeUp does not
//   exist) -> map it to fadeIn<Dir> / fadeOut<Dir>.
// - A directional type (slideIn/Out, bounceIn/Out, slideAndFade) with Direction
//   "None" would build a keyframe-less class (animate__slideIn) and just appear
//   -> fall back to Up on entry, Down on exit.
const resolveAnimationClass = (animation: string, direction: string, isEntry: boolean): string => {
	if (!animation) { return ''; }
	const dir = WIDGET_ANIMATION_TYPE_HAS_DIRECTION.includes(animation)
		? (direction || (isEntry ? 'Up' : 'Down'))
		: '';
	const name = animation === 'slideAndFade' ? (isEntry ? 'fadeIn' : 'fadeOut') : animation;
	return `animate__animated animate__${name}${dir}`;
};

const setAnimation = (
	el: AnimEl | null | undefined,
	animationClass: string,
	{ delay, duration }: { delay?: number; duration?: number } = {},
) => {
	if (!el) { return; }
	clearAnimation(el);
	if (!animationClass) { return; }
	animationClass.split(' ').filter(Boolean).forEach((c) => el.classList.add(c));
	if (delay != null) { el.style.setProperty('animation-delay', `${delay}ms`); }
	if (duration != null) { el.style.setProperty('animation-duration', `${duration}ms`); }
};

// Re-apply the idle loop to an already-mounted widget <g>. The editor strips ALL
// animate.css classes on drag/resize/rotate start (neutralizeWidgetAnimation) to measure
// the true box; once the interaction ends the loop must be put back. Clears any leftover
// animate classes first, then leaves the widget looping (or at rest if it has no loop).
export const restartLoopAnimation = (
	el: AnimEl | null | undefined,
	transformSettings?: { loopAnimation?: string; loopAnimationDuration?: number; loopAnimationDelay?: number },
) => {
	if (!el) { return; }
	clearAnimation(el);
	const { loopAnimation = '', loopAnimationDuration = 1000, loopAnimationDelay = 0 } = transformSettings ?? {};
	if (!loopAnimation) { return; }
	setAnimation(el, `animate__animated animate__${loopAnimation} animate__infinite`, { delay: loopAnimationDelay, duration: loopAnimationDuration });
};

// pill/square are the only animations with a staggered content sub-motion.
const CONTENT_MOTION: Partial<Record<string, boolean>> = {
	pillIn: true,
	squareIn: true,
	pillOut: true,
	squareOut: true,
};

// On entry the content SNAPS visible after this fraction of the shape's duration, then
// glides slowly to its place over the REST of the window - finishing exactly when the
// shape does (so "content settled" == "animation done"). Text leads the icon slightly
// and travels further (30px vs 14px) so its glide reads clearly. Exit / unlisted
// animations start with the shape (lead 0). See applyContentMotion.
const CONTENT_ENTRY_STAGGER: Partial<Record<string, { icon: number; text: number }>> = {
	pillIn: { text: 0.34, icon: 0.42 },
};

export function useWidgetAnimation({
	gElementRef,
	textElementRef,
	iconElementRef,
	bgElementRef,
	style,
	isShowWidget = true,
	isExiting,
	onExitDone,
	iconSlideFrom = 'left',
	isClickable = true,
	isEditing = false,
}: {
	gElementRef: { current: SVGGElement | null | undefined };
	// Content sub-motion targets (pill/square only). Animated with `margin` ONLY -
	// see the Safari rules above. Optional: widgets without text/icon omit them.
	textElementRef?: { current: HTMLElement | null | undefined };
	iconElementRef?: { current: HTMLElement | null | undefined };
	// Which side the icon slides in from. A left-anchored icon slides via margin-left;
	// a right-anchored one (button icon in a right corner) must slide via margin-right,
	// otherwise the margin is absorbed by the right anchor and it only fades in place.
	iconSlideFrom?: 'left' | 'right';
	// The element whose colour flashes to the active colour on press - a paint change,
	// so Safari-safe. A div (button/shape/hotspot bg) flashes `background-color`; an
	// <svg> (icon) flashes `fill`.
	bgElementRef?: { current: AnimEl | null | undefined };
	style: Pick<WidgetProps.StyleProps, 'transformSettings' | 'backgroundSettings'>;
	// Whether the playhead is inside the widget's time window. Drives re-entry: the
	// entry animation re-plays every time this goes true, which also clears any
	// leftover exit/press state (e.g. an interrupted press) so a widget that is
	// supposed to be visible always shows.
	isShowWidget?: boolean;
	isExiting?: boolean;
	onExitDone?: () => void;
	// The press ("active") animation only plays when the widget actually reacts to a
	// click, i.e. it has an event linked (same rule as the pointer cursor). A widget
	// with no event just runs its onComplete and never shows the active state.
	isClickable?: boolean;
	// Button text-edit mode: while editing, the idle loop is paused; it resumes once
	// editing ends. Only the Button widget passes this (default false = never editing).
	isEditing?: boolean;
}) {
	const isMountedRef = useRef(false);
	// pending press-finish timer, so it can be cancelled if the widget hides/unmounts
	const pressTimeoutRef = useRef<ReturnType<typeof setTimeout>>();
	// The resting colour to restore after a press flash. Captured ONCE when a press starts
	// from rest and reused by any overlapping press, so a rapid second press can never
	// capture the (already-active) colour and leave the button stuck on the active colour.
	const flashRestoreRef = useRef<{ el: AnimEl; prop: string; value: string } | null>(null);
	// Skip the edit-mode effect's first run so it never clobbers the entry animation on mount.
	const editModeInitRef = useRef(true);

	const {
		entryAnimation = '',
		entryAnimationDirection = '',
		entryAnimationDuration = 1000,
		entryAnimationDelay = 0,
		exitAnimation = '',
		exitAnimationDirection = '',
		exitAnimationDuration = 1000,
		exitAnimationDelay = 0,
		loopAnimation = '',
		loopAnimationDuration = 1000,
		loopAnimationDelay = 0,
	} = style.transformSettings ?? {};

	const activeBackgroundColor = style.backgroundSettings?.activeBackgroundColor;

	// Read via a ref so applyContentMotion stays stable: iconSlideFrom only affects
	// WHICH icon keyframe plays, it must never re-fire the entry/exit animation (e.g.
	// changing the icon or its placement should not replay the preview).
	const iconSlideFromRef = useRef(iconSlideFrom);
	iconSlideFromRef.current = iconSlideFrom;

	const entryAnimationClass = resolveAnimationClass(entryAnimation, entryAnimationDirection, true);
	const exitAnimationClass = resolveAnimationClass(exitAnimation, exitAnimationDirection, false);
	const loopAnimationClass = loopAnimation ? `animate__animated animate__${loopAnimation} animate__infinite` : '';

	// The shape (whole widget) animates the SVG <g> - see the Safari rules above.
	const shapeTarget = useCallback(() => gElementRef.current, [gElementRef]);

	// Content sub-motion (pill/square): text rises + fades (color), icon slides +
	// fades (fill) - margin/paint only (Safari). The content is time-synced to the
	// shape so it FINISHES exactly when the shape finishes: entry trails by
	// CONTENT_ENTRY_STAGGER then runs the rest of the window; exit runs the whole
	// window. Falling out of a pill/square (or switching type) clears stale classes.
	// The icon fade animates `fill`, which must sit on the <svg> itself (the icon has
	// an inline colour, so the container's colour never reaches it).
	const iconAnimEl = useCallback(
		(): AnimEl | null | undefined => iconElementRef?.current?.querySelector('svg') ?? iconElementRef?.current,
		[iconElementRef],
	);

	const applyContentMotion = useCallback(
		(animation: string, shapeDuration: number, shapeDelay: number, isEntry: boolean) => {
			const textEl = textElementRef?.current;
			const iconEl = iconAnimEl();
			if (!CONTENT_MOTION[animation]) {
				clearAnimation(textEl);
				clearAnimation(iconEl);
				return;
			}
			const stag = isEntry ? CONTENT_ENTRY_STAGGER[animation] : undefined;
			// lead by `start`, then glide over the REST of the window so content finishes
			// exactly with the shape. exit / unlisted: start with the shape (lead 0).
			const timing = (start: number) => ({
				delay: Math.round(shapeDelay + shapeDuration * start),
				duration: Math.round(shapeDuration * (1 - start)),
			});
			const suffix = isEntry ? 'In' : 'Out';
			const iconSide = iconSlideFromRef.current === 'right' ? 'Right' : '';
			setAnimation(textEl, `animate__animated animate__contentText${suffix}`, timing(stag?.text ?? 0));
			setAnimation(iconEl, `animate__animated animate__contentIcon${suffix}${iconSide}`, timing(stag?.icon ?? 0));
		},
		[textElementRef, iconAnimEl],
	);

	// Put the widget into its SHOWN state: play the entry (or loop, or just clear to
	// rest) + content-in. Used on (re-)entry and to restore after an exit PREVIEW.
	const showShape = useCallback(() => {
		const shapeEl = shapeTarget();
		if (entryAnimationClass) {
			setAnimation(shapeEl, entryAnimationClass, { delay: entryAnimationDelay, duration: entryAnimationDuration });
		} else if (loopAnimationClass) {
			setAnimation(shapeEl, loopAnimationClass, { delay: loopAnimationDelay, duration: loopAnimationDuration });
		} else {
			clearAnimation(shapeEl);
		}
		applyContentMotion(entryAnimation, entryAnimationDuration, entryAnimationDelay, true);
	}, [entryAnimation, entryAnimationClass, entryAnimationDelay, entryAnimationDuration, loopAnimationClass, loopAnimationDelay, loopAnimationDuration, shapeTarget, applyContentMotion]);

	// Just APPEAR at rest (no entry animation) - used to restore after an exit PREVIEW.
	// Replaying the entry there confused users ("out plays, then in plays again"); the
	// widget should simply reappear. Keeps the idle loop if one is set.
	const appearNow = useCallback(() => {
		const shapeEl = shapeTarget();
		if (loopAnimationClass) {
			setAnimation(shapeEl, loopAnimationClass, { delay: loopAnimationDelay, duration: loopAnimationDuration });
		} else {
			clearAnimation(shapeEl);
		}
		// content visible at rest (no content-in replay)
		clearAnimation(textElementRef?.current);
		clearAnimation(iconAnimEl());
	}, [loopAnimationClass, loopAnimationDelay, loopAnimationDuration, shapeTarget, textElementRef, iconAnimEl]);

	// Latest values as refs so the exit-PREVIEW effect can read them WITHOUT putting
	// them in its deps (it must run only when the exit SETTING changes, never on scrub).
	const appearNowRef = useRef(appearNow);
	appearNowRef.current = appearNow;
	const isShowWidgetRef = useRef(isShowWidget);
	isShowWidgetRef.current = isShowWidget;
	const isExitingRef = useRef(isExiting);
	isExitingRef.current = isExiting;

	// entry -> loop hand-off: when the entry animation ends, swap to loop
	useEffect(() => {
		const shapeEl = shapeTarget();
		const onAnimationEnd = (e: Event) => {
			// animationend can bubble - only hand off on the <g>'s own animation
			if (e.target !== shapeEl) { return; }
			if (loopAnimationClass) {
				setAnimation(shapeEl, loopAnimationClass, { delay: loopAnimationDelay, duration: loopAnimationDuration });
			}
		};
		shapeEl && shapeEl.addEventListener('animationend', onAnimationEnd);
		return () => {
			shapeEl && shapeEl.removeEventListener('animationend', onAnimationEnd);
		};
	}, [loopAnimationClass, loopAnimationDelay, loopAnimationDuration, shapeTarget]);

	// entry animation - runs on mount AND every time the widget (re-)enters its time
	// window (isShowWidget goes true). Re-playing entry also RESETS the shape: a
	// prior interrupted press or exit left an exit/press class (scale 0) on the <g>;
	// setAnimation clears it first, so a widget that should be visible always shows.
	useEffect(() => {
		if (!isShowWidget) { return; }
		// a re-entry supersedes any press still winding down
		clearTimeout(pressTimeoutRef.current);
		showShape();
	}, [isShowWidget, showShape]);

	// leaving the window (or unmounting) cancels a pending press so it can't fire
	// during the exit animation or after unmount and strand the widget.
	useEffect(() => {
		if (isShowWidget) { return; }
		clearTimeout(pressTimeoutRef.current);
	}, [isShowWidget]);
	useEffect(() => () => clearTimeout(pressTimeoutRef.current), []);

	// Button text-edit pauses the idle loop and resumes it when editing ends. Depends only
	// on isEditing (reads isShowWidget/appearNow via refs) so it never fires on re-entry -
	// otherwise it would clobber the entry animation with the rest state. Skips its first
	// run for the same reason.
	useEffect(() => {
		if (editModeInitRef.current) { editModeInitRef.current = false; return; }
		if (!isShowWidgetRef.current) { return; }
		if (isEditing) {
			clearAnimation(shapeTarget());
		} else {
			appearNowRef.current();
		}
	}, [isEditing, shapeTarget]);

	// exit-preview: reflect exit-setting changes (after mount) so editors see the
	// exit in the sidebar without scrubbing.
	useEffect(() => {
		if (!isMountedRef.current) {
			isMountedRef.current = true;
			return;
		}
		const shapeEl = shapeTarget();
		if (exitAnimationClass) {
			setAnimation(shapeEl, exitAnimationClass, { delay: exitAnimationDelay, duration: exitAnimationDuration });
		} else {
			clearAnimation(shapeEl);
		}
		applyContentMotion(exitAnimation, exitAnimationDuration, exitAnimationDelay, false);

		// This is only a PREVIEW of the exit (the setting changed while the playhead is
		// still inside the widget's window). Once it has played, restore the widget to
		// its shown state - otherwise pill/square (and any exit) leave it collapsed /
		// hidden. The real exit (isExiting) is handled separately and must stay gone.
		// Guards read from refs so this effect does NOT re-run (replay the exit) on scrub.
		if (!exitAnimationClass || !isShowWidgetRef.current || isExitingRef.current) { return; }
		const restoreAfter = (exitAnimationDelay || 0) + (exitAnimationDuration || 1000) + 100;
		const restoreTimer = setTimeout(() => { appearNowRef.current(); }, restoreAfter);
		return () => clearTimeout(restoreTimer);
	}, [exitAnimation, exitAnimationClass, exitAnimationDelay, exitAnimationDuration, shapeTarget, applyContentMotion]);

	// real exit (playhead passed the widget's end)
	useEffect(() => {
		if (!isExiting) {
			return;
		}
		const shapeEl = shapeTarget();
		if (!shapeEl || !exitAnimationClass) {
			onExitDone?.();
			return;
		}

		setAnimation(shapeEl, exitAnimationClass, { delay: exitAnimationDelay, duration: exitAnimationDuration });
		applyContentMotion(exitAnimation, exitAnimationDuration, exitAnimationDelay, false);

		let settled = false;
		let fallback: ReturnType<typeof setTimeout>;
		const settle = () => {
			if (settled) { return; }
			settled = true;
			clearTimeout(fallback);
			shapeEl.removeEventListener('animationend', handleEnd);
			onExitDone?.();
		};
		const handleEnd = (e: Event) => {
			if (e.target !== shapeEl) { return; }
			settle();
		};
		fallback = setTimeout(settle, 2000);
		shapeEl.addEventListener('animationend', handleEnd);
		return () => {
			clearTimeout(fallback);
			shapeEl.removeEventListener('animationend', handleEnd);
		};
	}, [isExiting, exitAnimation, exitAnimationClass, exitAnimationDelay, exitAnimationDuration, onExitDone, shapeTarget, applyContentMotion]);

	const triggerClickAnimation = useCallback((onComplete?: () => void) => {
		// No linked event -> no active/press animation (mirrors the pointer-cursor rule).
		const activeSuffix = isClickable ? ANIMATION_ACTIVE_SUFFIX[exitAnimation] : undefined;
		const pressEl = shapeTarget();
		if (!activeSuffix || !pressEl) {
			onComplete?.();
			return;
		}

		const activeDurationRatio = ACTIVE_DURATION_RATIO[exitAnimation];
		const activeDuration = activeDurationRatio ? exitAnimationDuration * activeDurationRatio : exitAnimationDuration;

		const textEl = textElementRef?.current;
		const iconEl = iconAnimEl();
		const bgEl = bgElementRef?.current;

		// flash the colour to the active colour (paint change - Safari-safe), restored
		// on finish. A div flashes background-color; an <svg> icon flashes fill.
		const flashProp = bgEl && bgEl.tagName.toLowerCase() === 'svg' ? 'fill' : 'background-color';
		if (bgEl && activeBackgroundColor) {
			// Capture the resting value only if not already mid-flash (an overlapping press
			// must not record the active colour as the value to restore to).
			if (!flashRestoreRef.current) {
				flashRestoreRef.current = { el: bgEl, prop: flashProp, value: bgEl.style.getPropertyValue(flashProp) };
			}
			bgEl.style.setProperty('transition', `${flashProp} ${ACTIVE_COLOR_FLASH_MS}ms ease-out`);
			bgEl.style.setProperty(flashProp, activeBackgroundColor);
		}

		let settled = false;
		const finish = () => {
			if (settled) { return; }
			settled = true;
			pressEl.removeEventListener('animationend', finish as EventListener);
			clearAnimation(pressEl);
			// content returns to its resting position as the loop resumes
			clearAnimation(textEl);
			clearAnimation(iconEl);
			// restore the flashed colour to the resting value captured at the first press
			const restore = flashRestoreRef.current;
			if (restore) {
				if (restore.value) { restore.el.style.setProperty(restore.prop, restore.value); }
				else { restore.el.style.removeProperty(restore.prop); }
				restore.el.style.removeProperty('transition');
				flashRestoreRef.current = null;
			}
			// resume the loop the press interrupted
			if (loopAnimationClass) {
				setAnimation(pressEl, loopAnimationClass, { delay: loopAnimationDelay, duration: loopAnimationDuration });
			}
			setTimeout(() => {
				onComplete?.();
			}, 500);
		};

		setAnimation(pressEl, `animate__animated animate__${activeSuffix}`, { delay: 0, duration: activeDuration });

		// content slides out with the collapse (starts at 54.3% of the press) and
		// finishes exactly when the press does
		const contentDelay = Math.round(activeDuration * 0.543);
		const contentDuration = Math.round(activeDuration - contentDelay);
		setAnimation(textEl, 'animate__animated animate__contentTextOut', { delay: contentDelay, duration: contentDuration });
		setAnimation(iconEl, `animate__animated animate__contentIconOut${iconSlideFromRef.current === 'right' ? 'Right' : ''}`, { delay: contentDelay, duration: contentDuration });

		clearTimeout(pressTimeoutRef.current);
		pressTimeoutRef.current = setTimeout(() => {
			finish();
		}, activeDuration + 200);
	}, [exitAnimation, exitAnimationDuration, activeBackgroundColor, loopAnimationClass, loopAnimationDelay, loopAnimationDuration, shapeTarget, textElementRef, iconAnimEl, bgElementRef, isClickable]);

	return { triggerClickAnimation };
}
